import React, { useEffect, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { classNames } from 'primereact/utils';
import { useForm } from './FormContext';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Check, ChevronDown } from 'lucide-react';
import { Appl } from '../../../../core/Appl';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
export enum StepStatus {
  Completed = 'completed',
  InProgress = 'in-progress',
  NotStarted = 'not-started',
  Disabled = 'disabled',
  Active = 'active',
  Error = 'error',
}

export interface SubStep {
  subStep: string;
  slug: string;
  nextButtonLabel?: string;
  cancelButtonLabel?: string;
  hideCancelButton?: boolean;
  hideNextButton?: boolean;
  element?: JSX.Element;
  // onStepChange?: () => void;
  //status?: 'completed' | 'in-progress' | 'not-started' | 'disabled' | 'review' | 'error' | undefined;
  status?: StepStatus;
  onStepChange?: (arg?: string) => Promise<boolean>;

}

export interface Step {
  step: string;
  slug: string;
  nextButtonLabel?: string;
  cancelButtonLabel?: string;
  hideCancelButton?: boolean;
  hideNextButton?: boolean;
  element?: JSX.Element;
  disabled?: boolean;
  subSteps: SubStep[];
  status?: StepStatus;
  submitted?: boolean;
  navigatedFromReviewScreen?: boolean;
  onStepChange?: (arg: string) => Promise<boolean>;
}

const SidebarFormNavigation: React.FC<{ retirementSteps: Step[] }> = observer(({ retirementSteps }) => {
  const { formData } = useForm();
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  const { memberId } = useParams();
  const [expandedSteps, setExpandedSteps] = useState<string[]>([]);

  // Update expanded steps when active step changes
  useEffect(() => {
    if (formData.activeStep?.slug) {
      setExpandedSteps([formData.activeStep.slug]);
    }
  }, [formData.activeStep]);

  // Modified helper function to check if a step is fully complete
  const isStepFullyComplete = (step: Step): boolean => {
    // If there are substeps, ALL must be complete for the step to be considered complete
    if (step.subSteps?.length) {
      return step.subSteps.every(subStep => subStep.status === StepStatus.Completed);
    }

    // If no substeps, then just check the main step status
    return step.status === StepStatus.Completed;
  };

  // Helper function to find the first incomplete step/substep
  const findFirstIncompleteStep = (steps: Step[]): { stepIndex: number; subStepIndex: number | null } => {
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const step = steps[stepIndex];

      // Check if the step is fully complete
      if (!isStepFullyComplete(step)) {
        // If there are substeps, find the first incomplete one
        if (step.subSteps?.length) {
          for (let subStepIndex = 0; subStepIndex < step.subSteps.length; subStepIndex++) {
            if (step.subSteps[subStepIndex].status !== StepStatus.Completed) {
              return { stepIndex, subStepIndex };
            }
          }
        }
        // If no substeps or couldn't find incomplete substep, return main step
        return { stepIndex, subStepIndex: null };
      }
    }
    return { stepIndex: -1, subStepIndex: null };
  };

  // Process steps to mark everything after first incomplete as NotStarted
  const processedSteps = React.useMemo(() => {
    if (!formData.retirementSteps) return [];
    

    const { stepIndex: firstIncompleteStepIndex, subStepIndex } = findFirstIncompleteStep(formData.retirementSteps);

    return formData.retirementSteps.map((step: Step, i: number) => {
      // Create a copy of the step to modify
      const updatedStep = { ...step };

      if (i < firstIncompleteStepIndex) {
        // Steps before the first incomplete step remain completed
        updatedStep.status = StepStatus.Completed;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map(sub => ({
            ...sub,
            status: StepStatus.Completed
          }));
        }
      } else if (i === firstIncompleteStepIndex) {
        // The first incomplete step is marked as active
        updatedStep.status = StepStatus.Active;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map((sub, j) => ({
            ...sub,
            status: j < subStepIndex! ? StepStatus.Completed :
              j === subStepIndex ? StepStatus.Active :
                StepStatus.NotStarted
          }));
        }
      } else {
        // All steps after the first incomplete step are marked as not started
        updatedStep.status = StepStatus.NotStarted;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map(sub => ({
            ...sub,
            status: StepStatus.NotStarted
          }));
        }
      }

      return updatedStep;
    });
  }, [formData, formData.activeStep]);

  const subStepIcon = (subStep: any) => {
    // let circleColor = '';
    let textColor = 'text-15px text-[#656565]';
    const stepIsActive = formData.activeSubStep && formData.activeSubStep.slug === subStep.slug;

    // if (stepIsActive) {
    //   circleColor = 'bg-primary-500';
    // }
    // else if (subStep.status === StepStatus.NotStarted) {
    //   circleColor = 'border-primary-500 bg-transparent border-2 ';
    // } else if (subStep.status === StepStatus.Completed) {
    //   circleColor = 'bg-primary-500';
    // } else if (subStep.status === StepStatus.Active) {
    //   circleColor = 'bg-yellow-500';
    // } else if (subStep.status === StepStatus.Error) {
    //   circleColor = 'bg-red-500';
    // }

    return (
      <>
        <div className={`ml-0.5 flex items-center w-full items-center pl-4 py-1 ${stepIsActive && 'bg-orange-50 '}`}>
          <div className="ml-2 mr-1 h-5 w-5 flex items-center">
            {subStep.status === StepStatus.Completed && <Check className='h-5 w-5 text-orange-500' />}
            {subStep.status === StepStatus.Active && <div className='h-4 w-4 rounded-full border-2 border-orange-500'></div>}
            {subStep.status === StepStatus.NotStarted && <div className='h-4 w-4 rounded-full border-2 border-gray-300'></div>}
          </div>
          <p className={`!text-[13px] ${textColor} ${subStep.status === StepStatus.Completed ? 'text-black' : ''} ${subStep.status === StepStatus.Active ? 'text-gray-800 font-medium' : ''}`}>
            {subStep.subStep}
          </p>
        </div>
      </>
    );
  };

  // Find the first incomplete step to determine which steps should be clickable
  const { stepIndex: firstIncompleteStepIndex } = findFirstIncompleteStep(processedSteps);

  const applicatedSubmittedFromLastStep = processedSteps[processedSteps.length - 1] && processedSteps[processedSteps.length - 1].submitted;

  return (
    <>
      {processedSteps.map(
        (
          step: {
            submitted: any;
            onStepChange: any;
            slug: any;
            completed: any;
            subSteps: any;
            step: React.ReactNode;
            status?: StepStatus;
            disabled?: boolean;
          },
          index: any,
        ) => {
          // Determine if this step should be clickable
          const isClickable = (index <= firstIncompleteStepIndex) && !applicatedSubmittedFromLastStep && !step.disabled;
          const isCompleted = step.status === StepStatus.Completed;
          const isActive = step.status === StepStatus.Active;


          return (
            <div key={index}>
              <Accordion.Root
                className={`${formData.activeStep && formData.activeStep.slug === step.slug ? ' ' : ''}`}
                type="multiple"
                disabled={!isClickable}
                value={expandedSteps}
                onValueChange={setExpandedSteps}>

                <AccordionItem value={step.slug} className="relative">
                  <AccordionTrigger hasSubSteps={step.subSteps && step.subSteps.length > 0}>

                    {index + 1 == formData.retirementSteps.length ? null : index < (formData && formData.activeStepNumber ? formData.activeStepNumber : 0) ? (
                      <div className={`absolute left-5 top-4  mt-0.5 h-full w-[1.5px] ${isClickable ? 'bg-primary-500' : 'bg-gray-300   '}`} aria-hidden="true" />
                    ) : index > (formData && formData.activeStepNumber ? formData.activeStepNumber : 0) ?
                      <div className="absolute left-5  top-4   mt-0.5 h-full w-[1.5px] bg-[#B3B3B3]" aria-hidden="true" /> : (index + 1 >= formData.retirementSteps.length ? null : (
                        <div className="absolute left-5  top-4   mt-0.5 h-full w-[1.5px] bg-orange-500" aria-hidden="true" />
                      ))}
                    {isClickable ? (
                      <Link
                        to={`${step.slug}`}
                        className="rounded-lg px-2 text-base w-full py-2 text-left group focus:outline-none focus-visible:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        tabIndex={step.subSteps && step.subSteps.length > 0 ? -1 : 0}
                        role="link"
                        aria-label={`Navigate to ${step.step}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            window.location.href = `${step.slug}`;
                          }
                        }}
                      >
                        <span className="flex items-start">
                          <span className="relative flex h-6 w-6 flex-shrink-0 items-center justify-center bg-white rounded-full">
                            {isCompleted ? (
                              <div className='h-6 w-6 flex items-center justify-center rounded-full'>
                                <svg className='text-primary-500 w-full h-full' viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11.5 2.15625C13.9781 2.15625 16.3547 3.14068 18.107 4.89297C19.8593 6.64526 20.8438 9.02188 20.8438 11.5C20.8438 13.9781 19.8593 16.3547 18.107 18.107C16.3547 19.8593 13.9781 20.8438 11.5 20.8438C9.02188 20.8438 6.64526 19.8593 4.89297 18.107C3.14068 16.3547 2.15625 13.9781 2.15625 11.5C2.15625 9.02188 3.14068 6.64526 4.89297 4.89297C6.64526 3.14068 9.02188 2.15625 11.5 2.15625ZM11.5 23C14.55 23 17.4751 21.7884 19.6317 19.6317C21.7884 17.4751 23 14.55 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0C8.45001 0 5.52494 1.2116 3.36827 3.36827C1.2116 5.52494 0 8.45001 0 11.5C0 14.55 1.2116 17.4751 3.36827 19.6317C5.52494 21.7884 8.45001 23 11.5 23ZM16.5762 9.38867C16.9984 8.96641 16.9984 8.28359 16.5762 7.86582C16.1539 7.44805 15.4711 7.44355 15.0533 7.86582L10.067 12.8521L7.95566 10.7408C7.5334 10.3186 6.85059 10.3186 6.43281 10.7408C6.01504 11.1631 6.01055 11.8459 6.43281 12.2637L9.30781 15.1387C9.73008 15.5609 10.4129 15.5609 10.8307 15.1387L16.5762 9.38867Z" fill="currentColor" />
                                </svg>
                              </div>
                            ) : isActive ? (
                              <div className='bg-orange-500 h-6 w-6 flex items-center justify-center rounded-full' />
                            ) : (
                              <div className="h-full w-full h-6 w-6">
                                <div className="h-full w-full border-[#B3B3B3] bg-transparent border-2 rounded-full" aria-hidden="true" />
                              </div>
                            )}
                          </span>

                          <span className={`ml-3 text-15px font-semibold 
                          ${isCompleted ? 'text-primary-500' : ''}
                          ${isActive ? 'text-gray-600' : ''}
                          ${!isCompleted && !isActive ? 'text-gray-400' : ''}
                        `}>
                            {step.step}
                          </span>
                        </span>
                      </Link>
                    ) : (
                      <div className="rounded-lg px-2 text-base w-full py-2 text-left group cursor-not-allowed  ">
                        <span className="flex items-start">
                          <span className="relative flex h-6 w-6 flex-shrink-0 items-center justify-center bg-white rounded-full">
                            {step.submitted ? <div className='h-6 w-6 flex items-center justify-center rounded-full'>
                              <svg className='text-primary-500 w-full h-full' viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.5 2.15625C13.9781 2.15625 16.3547 3.14068 18.107 4.89297C19.8593 6.64526 20.8438 9.02188 20.8438 11.5C20.8438 13.9781 19.8593 16.3547 18.107 18.107C16.3547 19.8593 13.9781 20.8438 11.5 20.8438C9.02188 20.8438 6.64526 19.8593 4.89297 18.107C3.14068 16.3547 2.15625 13.9781 2.15625 11.5C2.15625 9.02188 3.14068 6.64526 4.89297 4.89297C6.64526 3.14068 9.02188 2.15625 11.5 2.15625ZM11.5 23C14.55 23 17.4751 21.7884 19.6317 19.6317C21.7884 17.4751 23 14.55 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0C8.45001 0 5.52494 1.2116 3.36827 3.36827C1.2116 5.52494 0 8.45001 0 11.5C0 14.55 1.2116 17.4751 3.36827 19.6317C5.52494 21.7884 8.45001 23 11.5 23ZM16.5762 9.38867C16.9984 8.96641 16.9984 8.28359 16.5762 7.86582C16.1539 7.44805 15.4711 7.44355 15.0533 7.86582L10.067 12.8521L7.95566 10.7408C7.5334 10.3186 6.85059 10.3186 6.43281 10.7408C6.01504 11.1631 6.01055 11.8459 6.43281 12.2637L9.30781 15.1387C9.73008 15.5609 10.4129 15.5609 10.8307 15.1387L16.5762 9.38867Z" fill="currentColor" />
                              </svg>
                            </div> : <div className="h-full w-full h-6 w-6">
                              <div className="h-full w-full border-[#B3B3B3] bg-transparent border-2 rounded-full" aria-hidden="true" />
                            </div>}
                          </span>
                          <span className={`ml-3 text-15px font-semibold ${step.submitted ? 'text-primary-500' : 'text-gray-400'}`}>
                            {step.step}
                          </span>
                        </span>
                      </div>
                    )}

                    {' '}{step.subSteps.length > 0 && isClickable && <ChevronDown className={`${isClickable && '-ml-5 hover:scale-[1.05] hover:text-gray-600 '} w-5 h-5 text-gray-400 transform transition-transform duration-200 group-data-[state=open]:rotate-180 `} />}

                  </AccordionTrigger>

                  {step.subSteps && step.subSteps.length > 0 && (
                    <AccordionContent>
                      {step.subSteps.map(
                        (
                          subStep: {
                            slug: any;
                            completed: any;
                            subStep: React.ReactNode;
                            status: StepStatus,
                          },
                          subIndex: any,
                        ) => {
                          const isSubStepClickable = isClickable && (
                            subStep.status === StepStatus.Completed ||
                            subStep.status === StepStatus.Active ||
                            (subIndex === 0 && isActive)
                          );

                          return (
                            <div key={subIndex} className="relative pl-5">
                              {isSubStepClickable ? (
                                <Link
                                  to={`${step.slug}/${subStep.slug}`}
                                  className={`${formData.activeSubStep && formData.activeSubStep.slug === subStep.slug ? ' rounded-lg text-black font-medium' : 'text-gray-700'}
                                py-1 flex items-center space-x-2 !text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-md relative z-10`}
                                >
                                  {subStepIcon(subStep)}
                                </Link>
                              ) : (
                                <div className="py-1 flex items-center space-x-2 cursor-not-allowed opacity-70 !text-[13px]">
                                  {subStepIcon(subStep)}
                                </div>
                              )}
                            </div>
                          );
                        },
                      )}
                    </AccordionContent>
                  )}
                </AccordionItem>
              </Accordion.Root>
            </div>
          )
        })}
    </>
  );
});

type AccordionItemProps = React.ComponentProps<typeof Accordion.Item> & {
  className?: string;
};

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Item className={classNames(className)} {...props} ref={forwardedRef}>
    {children}
  </Accordion.Item>
));

type AccordionTriggerProps = React.ComponentProps<typeof Accordion.Trigger> & {
  className?: string;
  hasSubSteps?: boolean;
};

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, hasSubSteps, ...props }, forwardedRef) => (
    <Accordion.Header className="flex">
      <Accordion.Trigger
        className={classNames('group flex flex-1 cursor-pointer items-center justify-between py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-md', className)}
        {...props}
        ref={forwardedRef}
        tabIndex={hasSubSteps ? 0 : -1}
      >
        {children}
      </Accordion.Trigger>
    </Accordion.Header>
  ),
);

type AccordionContentProps = React.ComponentProps<typeof Accordion.Content> & {
  className?: string;
};

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Content
      className={classNames(
        'overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="pb-4 py-1 pr-1">{children}</div>
    </Accordion.Content>
  ),
);

export default SidebarFormNavigation;
